import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Coins, FileWarning, PackageSearch, Truck } from 'lucide-react';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import DetalleOperativoTable from '@/components/reportes/DetalleOperativoTable.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  fetchReporteAlertasOperativas,
  fetchReporteCobranzasOperativas,
  fetchReporteFacturacionOperativa,
  fetchReporteGuiasPendientes,
  fetchReporteRepartosOperativos,
} from '@/services/reportes.js';

const moneyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

const sectionConfig = {
  guias: {
    title: 'Guías',
    icon: PackageSearch,
    color: 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]',
    metrics: [
      { key: 'guias_sin_valorizar', label: 'Sin valorizar' },
      { key: 'guias_valorizadas_sin_facturar', label: 'Valorizadas sin facturar' },
      { key: 'guias_en_reparto_sin_cerrar', label: 'En reparto sin cerrar' },
      { key: 'guias_retenidas', label: 'Retenidas' },
      { key: 'guias_en_foja_no_recibidas', label: 'En foja no recibidas' },
    ],
  },
  repartos: {
    title: 'Repartos',
    icon: Truck,
    color: 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]',
    metrics: [
      { key: 'repartos_abiertos', label: 'Abiertos' },
      { key: 'repartos_sin_rendicion', label: 'Sin rendición' },
      { key: 'repartos_con_diferencias', label: 'Con diferencias' },
    ],
  },
  cobranzas: {
    title: 'Cobranzas',
    icon: Coins,
    color: 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]',
    metrics: [
      { key: 'minutas_abiertas', label: 'Minutas abiertas' },
      { key: 'cobranzas_no_rendidas', label: 'Cobranzas no rendidas' },
      { key: 'diferencia_cobrado_vs_rendido', label: 'Diferencias cobrado vs rendido' },
    ],
  },
  facturacion: {
    title: 'Facturación',
    icon: FileWarning,
    color: 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]',
    metrics: [
      { key: 'guias_facturadas_con_diferencia', label: 'Facturadas con diferencia' },
      { key: 'guias_facturadas_parcialmente', label: 'Facturadas parcialmente' },
      { key: 'guias_sin_facturar', label: 'Sin facturar' },
    ],
  },
};

const getSeverity = (count) => {
  if (count <= 0) return { tone: 'verde', dot: 'bg-[rgb(var(--success))]', ring: 'border-[rgba(var(--success),0.28)]', text: 'OK' };
  if (count <= 5) return { tone: 'amarillo', dot: 'bg-[rgb(var(--warning))]', ring: 'border-[rgba(var(--warning),0.32)]', text: 'Atención' };
  return { tone: 'rojo', dot: 'bg-[rgb(var(--danger))]', ring: 'border-[rgba(var(--danger),0.28)]', text: 'Problema' };
};

const sumTotals = (data = {}, keys = []) => keys.reduce((sum, key) => sum + Number(data?.[key]?.total || 0), 0);
const formatMoney = (value) => moneyFormatter.format(Number(value || 0));
const formatDate = (value) => value || '—';

const getExtraColumns = (metricKey) => {
  if (metricKey === 'diferencia_cobrado_vs_rendido' || metricKey === 'repartos_con_diferencias' || metricKey === 'guias_facturadas_con_diferencia') {
    return [{ key: 'diferencia_label', label: 'Diferencia' }];
  }
  if (metricKey === 'cobranzas_no_rendidas' || metricKey === 'minutas_abiertas' || metricKey === 'guias_facturadas_parcialmente') {
    return [{ key: 'monto_label', label: 'Monto' }];
  }
  return [];
};

function DashboardCard({ title, count, severity, onClick, icon: Icon, color }) {
  return (
    <article className={`rounded-2xl border ${severity.ring} bg-[rgb(var(--panel))] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.12)]`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[rgb(var(--muted))]">{title}</p>
          <p className="mt-2 text-3xl font-semibold text-[rgb(var(--fg))]">{count}</p>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`h-3 w-3 rounded-full ${severity.dot}`} />
          <span className="text-sm text-[rgba(var(--fg),0.78)]">{severity.text}</span>
        </div>
        <button
          type="button"
          onClick={onClick}
          className="rounded-full border border-[rgba(var(--border),0.7)] px-3 py-1 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)]"
        >
          Ver detalle
        </button>
      </div>
    </article>
  );
}

export default function DashboardOperativo() {
  const { sucursalActiva } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState({
    guias: null,
    repartos: null,
    cobranzas: null,
    facturacion: null,
    alertas: null,
  });
  const [activeSection, setActiveSection] = useState('guias');
  const [activeMetric, setActiveMetric] = useState('guias_sin_valorizar');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [guias, repartos, cobranzas, facturacion, alertas] = await Promise.all([
          fetchReporteGuiasPendientes(),
          fetchReporteRepartosOperativos(),
          fetchReporteCobranzasOperativas(),
          fetchReporteFacturacionOperativa(),
          fetchReporteAlertasOperativas(),
        ]);
        if (!mounted) return;
        setPayload({ guias, repartos, cobranzas, facturacion, alertas });
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || err?.response?.data?.message || 'No se pudieron cargar los reportes operativos.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [sucursalActiva?.id]);

  const dashboardCards = useMemo(() => {
    const dataBySection = {
      guias: payload.guias,
      repartos: payload.repartos,
      cobranzas: payload.cobranzas,
      facturacion: payload.facturacion,
    };

    return Object.entries(sectionConfig).map(([key, config]) => {
      const count = sumTotals(dataBySection[key], config.metrics.map((metric) => metric.key));
      return {
        key,
        title: config.title,
        icon: config.icon,
        color: config.color,
        count,
        severity: getSeverity(count),
      };
    });
  }, [payload]);

  const activeConfig = sectionConfig[activeSection];
  const activeData = payload[activeSection] || {};
  const activeMetricConfig = activeConfig.metrics.find((metric) => metric.key === activeMetric) || activeConfig.metrics[0];
  const activeBucket = activeData?.[activeMetricConfig.key] || { total: 0, por_sucursal: [], items: [] };

  const detailRows = useMemo(
    () =>
      (activeBucket.items || []).map((item) => ({
        ...item,
        fecha: formatDate(item.fecha),
        monto_label: item.monto == null ? '—' : formatMoney(item.monto),
        diferencia_label: item.diferencia == null ? '—' : formatMoney(item.diferencia),
      })),
    [activeBucket],
  );

  const alertasCriticas = Number(payload.alertas?.eventos_criticos?.total || 0);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,rgba(var(--bg),1)_0%,rgba(var(--panel),0.92)_42%,rgba(var(--bg),0.96)_100%)]">
      <div className="mx-auto flex w-full max-w-[1380px] flex-col gap-4 px-4 pb-6 pt-4 md:px-5">
        <PageHeaderCompact
          title="Dashboard Operativo"
          description="Pendientes e inconsistencias sobre datos persistidos de operación, cobranzas y facturación."
        />

        {alertasCriticas > 0 ? (
          <section className="rounded-2xl border border-[rgba(var(--brand),0.22)] bg-[rgba(var(--brand),0.1)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-[0_16px_30px_rgba(var(--shadow),0.12)]">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5" />
              <span>{alertasCriticas} alertas críticas activas superan ventanas operativas.</span>
            </div>
          </section>
        ) : null}

        {error ? (
          <section className="rounded-2xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]">
            {error}
          </section>
        ) : null}

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {dashboardCards.map((card) => (
            <DashboardCard
              key={card.key}
              title={card.title}
              count={card.count}
              severity={card.severity}
              icon={card.icon}
              color={card.color}
              onClick={() => {
                setActiveSection(card.key);
                setActiveMetric(sectionConfig[card.key].metrics[0].key);
              }}
            />
          ))}
        </section>

        <section className="grid gap-4 xl:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.12)]">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[rgb(var(--muted))]">Detalle activo</p>
                <h2 className="mt-1 text-xl font-semibold text-[rgb(var(--fg))]">{activeConfig.title}</h2>
              </div>
              {loading ? <span className="text-sm text-[rgb(var(--muted))]">Cargando…</span> : null}
            </div>

            <div className="space-y-3">
              {activeConfig.metrics.map((metric) => {
                const bucket = activeData?.[metric.key] || { total: 0 };
                const severity = getSeverity(bucket.total);
                const isActive = metric.key === activeMetric;
                return (
                  <button
                    key={metric.key}
                    type="button"
                    onClick={() => setActiveMetric(metric.key)}
                    className={`w-full rounded-2xl border px-3.5 py-3 text-left transition ${
                      isActive
                        ? 'border-[rgb(var(--fg))] bg-[rgb(var(--fg))] text-[rgb(var(--bg))]'
                        : 'border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.72)] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.16)]'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">{metric.label}</span>
                      <span className={`h-3 w-3 rounded-full ${isActive ? 'bg-white' : severity.dot}`} />
                    </div>
                    <div className="mt-2 text-2xl font-semibold">{bucket.total || 0}</div>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="flex min-h-[480px] flex-col gap-4">
            <div className="grid gap-4 lg:grid-cols-[1.3fr_0.7fr]">
              <article className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.12)]">
                <p className="text-xs uppercase tracking-[0.18em] text-[rgb(var(--muted))]">Indicador</p>
                <h3 className="mt-1 text-2xl font-semibold text-[rgb(var(--fg))]">{activeMetricConfig.label}</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <div>
                    <p className="text-sm text-[rgb(var(--muted))]">Total casos</p>
                    <p className="mt-2 text-4xl font-semibold text-[rgb(var(--fg))]">{activeBucket.total || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[rgb(var(--muted))]">Monto asociado</p>
                    <p className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{formatMoney(activeBucket.monto_total || 0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[rgb(var(--muted))]">Diferencia total</p>
                    <p className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{formatMoney(activeBucket.diferencia_total || 0)}</p>
                  </div>
                </div>
              </article>

              <article className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.12)]">
                <p className="text-xs uppercase tracking-[0.18em] text-[rgb(var(--muted))]">Sucursales</p>
                <div className="mt-4 space-y-3">
                  {(activeBucket.por_sucursal || []).slice(0, 5).map((item) => (
                    <div key={`${item.sucursal_id}-${item.sucursal}`} className="rounded-2xl bg-[rgba(var(--panel),0.66)] px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-[rgba(var(--fg),0.82)]">{item.sucursal}</span>
                        <span className="text-lg font-semibold text-[rgb(var(--fg))]">{item.total}</span>
                      </div>
                    </div>
                  ))}
                  {!activeBucket.por_sucursal?.length ? (
                    <p className="text-sm text-[rgb(var(--muted))]">Sin sucursales con pendientes.</p>
                  ) : null}
                </div>
              </article>
            </div>

            <DetalleOperativoTable
              rows={detailRows}
              extraColumns={getExtraColumns(activeMetric)}
              emptyMessage="Sin pendientes para este criterio."
            />
          </div>
        </section>
      </div>
    </div>
  );
}
